import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import {
  LocalSectionFields,
  emptyLocalSection,
  localFieldsFrom,
  localSectionFrom,
  type FooterField,
  type LocalSectionValue,
} from '../components/LocalSectionFields.js';
import { SubmitRefusalLine, useSubmitRefusal } from '../components/SubmitRefusal.js';
import { RefusalNotice, useWritesRefused } from '../components/SuspendedNotice.js';
import { Alert, Button, Card, TextField } from '../components/ui.js';
import { ApiError } from '../lib/api.js';
import { FIELD_LIMITS } from '../lib/fieldLimits.js';
import {
  createClientProfile,
  fetchClientProfile,
  updateClientProfile,
  type ClientProfile,
} from '../lib/ordersApi.js';

/**
 * The project form mirrors intake v2: target information and the local section,
 * and nothing else.
 *
 * **Link choices are not here.** They are decided per order and stored on the
 * order, so a project profile has no opinion about where a build links.
 *
 * The round-1 fields this form used to carry — services, USPs, brand rules,
 * things to avoid, footer text, target queries — are no longer edited here.
 * Their columns still hold whatever they held: this form simply does not send
 * those keys, and an absent key means "leave that column exactly as it is".
 */

interface FormState {
  businessName: string;
  websiteUrl: string;
  local: LocalSectionValue;
}

const EMPTY: FormState = {
  businessName: '',
  websiteUrl: '',
  local: emptyLocalSection(),
};

function toForm(profile: ClientProfile): FormState {
  return {
    businessName: profile.businessName,
    websiteUrl: profile.websiteUrl ?? '',
    local: localSectionFrom(profile),
  };
}

interface FormErrors {
  businessName?: string;
  websiteUrl?: string;
  targetLocation?: string;
  footer?: Partial<Record<FooterField, string>>;
}

const FOOTER_FIELDS = new Set<string>([
  'businessName',
  'phone',
  'addressLine1',
  'addressLine2',
  'city',
  'region',
  'postcode',
  'country',
  'mapsEmbed',
]);

/** Server field paths, mapped back onto the control that owns them. */
function errorsFrom(issues: { field: string; message: string }[]): FormErrors {
  const errors: FormErrors = {};

  for (const issue of issues) {
    const [head, second] = issue.field.split('.');

    if (head === 'businessName') errors.businessName = issue.message;
    else if (head === 'websiteUrl') errors.websiteUrl = issue.message;
    else if (head === 'targetLocation') errors.targetLocation = issue.message;
    else if (head === 'footer' && second !== undefined && FOOTER_FIELDS.has(second)) {
      errors.footer = { ...errors.footer, [second]: issue.message };
    }
  }

  return errors;
}

export function ClientFormPage(): React.JSX.Element {
  const { clientProfileId } = useParams<{ clientProfileId: string }>();
  const isEdit = Boolean(clientProfileId);
  const navigate = useNavigate();

  const [form, setForm] = useState<FormState>(EMPTY);
  const [fieldErrors, setFieldErrors] = useState<FormErrors>({});
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(isEdit);
  // WAIT-PL ruling 1: a refusal is shown at the button and moves to the field.
  const refusal = useSubmitRefusal();

  /*
   * **WAIT-B round 2, defect 3**, and the one page where the two halves of the
   * form answer differently.
   *
   * `POST /api/client-profiles` carries `requireAgencyState('active')` -
   * creating a project is preparing to order, and a suspended account cannot
   * order. `PATCH /api/client-profiles/:id` carries no state guard at all, by
   * the same ruling: *"reading and editing existing ones stays open"*. So the
   * same screen is `'active-only'` when adding and `'any-state'` when editing,
   * and a suspended agency correcting a phone number is not stopped by a
   * sentence about ordering.
   *
   * Under view-as both are refused, which is why the notice is the same
   * component rather than two.
   */
  const { refused } = useWritesRefused(isEdit ? 'any-state' : 'active-only');

  useEffect(() => {
    if (!clientProfileId) return;
    let cancelled = false;
    void (async () => {
      try {
        const response = await fetchClientProfile(clientProfileId);
        if (!cancelled) setForm(toForm(response.clientProfile));
      } catch {
        if (!cancelled) setError('We could not load this project.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [clientProfileId]);

  function update(field: 'businessName' | 'websiteUrl') {
    return (event: React.ChangeEvent<HTMLInputElement>) => {
      setForm((current) => ({ ...current, [field]: event.target.value }));
    };
  }

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setError(null);
    setFieldErrors({});
    refusal.clear();
    setBusy(true);

    const local = localFieldsFrom(form.local);
    const payload = {
      businessName: form.businessName.trim(),
      websiteUrl: form.websiteUrl.trim(),
      targetLocation: local.targetLocation,
      footer: local.footer,
    };

    try {
      const response = clientProfileId
        ? await updateClientProfile(clientProfileId, payload)
        : await createClientProfile(payload);
      void navigate(`/projects/${response.clientProfile.id}`, { replace: true });
    } catch (caught) {
      if (caught instanceof ApiError) {
        const mapped = errorsFrom(caught.fieldIssues);
        setFieldErrors(mapped);
        if (Object.keys(mapped).length > 0) {
          setError('Please check the highlighted fields.');
          refusal.refuseFields();
        } else {
          setError(caught.message);
          refusal.refuse(caught.message);
        }
      } else {
        setError('We could not save this project. Please try again.');
        refusal.refuse('We could not save this project. Please try again.');
      }
    } finally {
      setBusy(false);
    }
  }

  if (loading) {
    return (
      <p className="text-sm text-fg-muted" role="status">
        Loading…
      </p>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <Link to="/projects" className="text-sm text-fg-muted underline">
          Back to projects
        </Link>
        <h1 className="mt-2 text-2xl font-semibold tracking-tight">
          {isEdit ? 'Edit project' : 'Add a project'}
        </h1>
        <p className="mt-1 text-sm text-fg-muted">
          Reusable details for one of the targets you order for. Only the name is required, and
          orders already placed keep the details they were placed with.
        </p>
      </div>

      <form
        ref={refusal.formRef}
        onSubmit={(event) => void handleSubmit(event)}
        className="space-y-6"
        noValidate
      >
        {error ? <Alert>{error}</Alert> : null}
        {refused ? <RefusalNotice action="add a project" /> : null}

        {/*
          **A `fieldset` rather than a prop on each control.** The local section
          is its own component with its own fields, and threading `disabled`
          through it would leave the next field somebody adds there live by
          default. A disabled fieldset disables every control inside it, now and
          later, which is the behaviour this guard is trying to have.
        */}
        <fieldset disabled={refused} className="space-y-6">
        <Card className="space-y-5">
          <div>
            <h2 className="text-sm font-semibold text-fg-muted">Target information</h2>
            <p className="mt-1 text-sm text-fg-muted">
              What we need to know about the brand or site this build is for.
            </p>
          </div>

          <TextField
            label="Name"
            hint="The brand, business, or site this build is targeting."
            name="businessName"
            required
            maxLength={FIELD_LIMITS.businessName}
            value={form.businessName}
            onChange={update('businessName')}
            error={fieldErrors.businessName}
          />

          <TextField
            label="Main URL"
            hint="Website, store, or profile."
            name="websiteUrl"
            placeholder="https://harbourpointplumbing.example"
            value={form.websiteUrl}
            onChange={update('websiteUrl')}
            error={fieldErrors.websiteUrl}
          />
        </Card>

        <Card className="space-y-5">
          <div>
            <h2 className="text-sm font-semibold text-fg-muted">Local business?</h2>
            <p className="mt-1 text-sm text-fg-muted">
              Give a target location and we build the site around it.
            </p>
          </div>

          <LocalSectionFields
            value={form.local}
            onChange={(local) => setForm((current) => ({ ...current, local }))}
            targetName={form.businessName}
            errors={{ targetLocation: fieldErrors.targetLocation, footer: fieldErrors.footer }}
          />
        </Card>

        <SubmitRefusalLine message={refusal.message} />

        <div className="flex items-center gap-3">
          <Button type="submit" busy={busy}>
            {isEdit ? 'Save changes' : 'Add project'}
          </Button>
          <Link to="/projects" className="text-sm text-fg-muted underline">
            Cancel
          </Link>
        </div>
        </fieldset>
      </form>
    </div>
  );
}
